"use client";

import React from "react";
import { Activity, TriangleAlert as AlertTriangle, ArrowRight, Droplets, Gauge, TrendingDown } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
interface DiagnosticCard {
  id: number;
  title: string;
  icon: typeof Gauge;
  trigger: string;
  description: string;
  severity: string;
}
const diagnosticCards: DiagnosticCard[] = [{
  id: 1,
  title: "Low Water Pressure",
  icon: Gauge,
  trigger: "Mineral Scale or PRV Fault",
  description: "A weak trickle from single faucets or whole-home flow loss usually signals clogged aerators, failing pressure regulators, or corroded supply lines.",
  severity: "Common Issue"
}, {
  id: 2,
  title: "No Water Pressure",
  icon: AlertTriangle,
  trigger: "Main Line or Valve Cutoff",
  description: "A complete loss of running water indicates an accidental main shutoff closure, catastrophic pipe burst, or severe municipal line disruption.",
  severity: "Urgent Diagnostic"
}, {
  id: 3,
  title: "High Water Pressure",
  icon: Droplets,
  trigger: "PRV Failure (>80 PSI)",
  description: "Overpressurized plumbing stresses pipe joints, causes noisy water hammer, drips water heater relief valves, and damages expensive home appliances.",
  severity: "System Risk"
}, {
  id: 4,
  title: "Water Pressure Drops",
  icon: TrendingDown,
  trigger: "Hidden Leak or Demand Spike",
  description: "Pressure that plunges whenever a shower or washing machine runs suggests undersized branch piping, partial line blockages, or active slab leaks.",
  severity: "Intermittent Fault"
}];
export default function WaterPressure() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };
  return <section id="waterpressureproblems" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary text-secondary-foreground text-xs font-semibold uppercase tracking-wider mb-4">
            <Activity className="size-3.5 text-accent" />
            <span>Emergency Diagnostic Triage</span>
          </div>

          <h2 className="font-default text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground mb-4">
            Water Pressure Problems
          </h2>

          <p className="font-default text-base sm:text-lg text-muted-foreground leading-relaxed">
            Inconsistent or failing water pressure signals hidden plumbing strain.
            Identify your symptoms below to get fast, targeted emergency repair
            from licensed Jonesboro plumbers.
          </p>
        </div>

        {/* 4-Column Diagnostic Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {diagnosticCards.map((card, index) => {
          const IconComponent = card.icon;
          return <motion.div key={card.id} variants={cardVariants} data-index={index}>
                <Card className="h-full bg-card border border-border shadow-sm hover:shadow-md hover:border-border/80 transition-shadow duration-200 flex flex-col justify-between">
                  <div>
                    <CardHeader className="space-y-4 pb-4">
                      <div className="flex items-center justify-between gap-2">
                        <div className="size-12 rounded-lg bg-secondary flex items-center justify-center text-primary">
                          <IconComponent className="size-6" />
                        </div>
                        <Badge variant="secondary" className="text-[11px] font-medium tracking-normal">
                          {card.severity}
                        </Badge>
                      </div>

                      <CardTitle className="font-default text-xl font-bold text-card-foreground">
                        {card.title}
                      </CardTitle>
                    </CardHeader>

                    <CardContent className="space-y-4">
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {card.description}
                      </p>

                      <div className="pt-2 border-t border-border/60">
                        <span className="block text-xs font-semibold text-foreground mb-1 uppercase tracking-wider">
                          Likely Root Cause
                        </span>
                        <span className="inline-block text-xs font-medium text-muted-foreground bg-muted/60 px-2.5 py-1 rounded-md">
                          {card.trigger}
                        </span>
                      </div>
                    </CardContent>
                  </div>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Action Button */}
        <div className="mt-12 md:mt-16 text-center flex flex-col sm:flex-row items-center justify-center gap-4">
          <Button asChild size="lg" className="w-full sm:w-auto font-medium shadow-xs">
            <Link to="/services/" className="inline-flex items-center gap-2">
              <span>View Water Pressure Problems</span>
              <ArrowRight className="size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}